import type {
  ObservationsMapParameterDisplayGroup,
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesConfigurationBody,
  ParametersUnit,
} from "@/lib/api/generated/types";
import type {
  ParameterWithPropertyKind,
  ParameterWithUnits,
  UnitFamilyMap,
} from "@/lib/parameters/parameter-units.utils";
import {
  configuredTimeSeriesParameterIdSet,
  familyUnitsForKind,
  pickAutoUnitCode,
  resolveUnitFromCatalog,
} from "@/lib/parameters/parameter-units.utils";

/** Catalog entry the unit backfill needs: an id, its units, and its property kind. */
export type BackfillCatalogParameter = ParameterWithUnits &
  ParameterWithPropertyKind & { id: string };

const EMPTY_FAMILIES: UnitFamilyMap = new Map();

/**
 * Loaded chart config (`unit`) or an already-converted save body (`unit_code`).
 * Save normalizers run more than once on the same payload (the map dialogs
 * normalize on submit, then `isUnchangedWidgetUpdate` normalizes again), so the
 * conversion accepts its own output.
 */
type ParameterTimeSeriesConfigurationLike =
  | ParameterTimeSeriesConfiguration
  | ParameterTimeSeriesConfigurationBody;

/**
 * `unit` is nullable in the spec and absent once converted, so a missing unit
 * yields an empty code instead of throwing; the CMS already blocks saving a
 * group without a unit.
 */
function readUnitCode(config: ParameterTimeSeriesConfigurationLike): string {
  const record = config as { unit?: ParametersUnit | null; unit_code?: string };
  return record.unit?.code ?? record.unit_code ?? "";
}

export function toParameterTimeSeriesConfigurationBody(
  config: ParameterTimeSeriesConfigurationLike,
): ParameterTimeSeriesConfigurationBody {
  return {
    title: config.title,
    visualization_type: config.visualization_type,
    unit_code: readUnitCode(config),
    series: config.series,
    ...(config.thresholds && config.thresholds.length > 0
      ? { thresholds: config.thresholds }
      : {}),
  };
}

export function normalizeParameterTimeSeriesConfigurationsForSave(
  configurations: ParameterTimeSeriesConfigurationLike[] | undefined,
): ParameterTimeSeriesConfigurationBody[] {
  if (!Array.isArray(configurations)) return [];
  return configurations.map(toParameterTimeSeriesConfigurationBody);
}

/**
 * Fills a missing group unit (legacy configs saved before the unit was required)
 * from the group's property-kind unit family. The group's quantity is taken from
 * its first resolvable series parameter; the family comes from the catalog, or
 * falls back to that parameter's own units when the catalog is unavailable.
 */
export function backfillParameterTimeSeriesUnit(
  config: ParameterTimeSeriesConfiguration,
  catalog: BackfillCatalogParameter[],
  families: UnitFamilyMap = EMPTY_FAMILIES,
): ParameterTimeSeriesConfiguration {
  if (config.unit?.code) return config;

  const first = config.series
    .map((series) => catalog.find((parameter) => parameter.id === series.parameter_id))
    .find((parameter): parameter is BackfillCatalogParameter => !!parameter);

  if (!first) return config;

  const familyUnits = familyUnitsForKind(first.propertyKindId, families, first.units);
  const autoCode = pickAutoUnitCode(familyUnits, undefined);
  const fallbackCode = autoCode ?? first.units[0]?.code ?? "";
  const unit =
    resolveUnitFromCatalog(
      familyUnits.length > 0 ? familyUnits : first.units,
      fallbackCode,
    ) ?? config.unit;

  if (!unit) return config;

  return { ...config, unit };
}

export function backfillParameterTimeSeriesConfigurations(
  configurations: ParameterTimeSeriesConfiguration[] | undefined,
  catalog: BackfillCatalogParameter[],
  families: UnitFamilyMap = EMPTY_FAMILIES,
): ParameterTimeSeriesConfiguration[] {
  if (!Array.isArray(configurations)) return [];
  return configurations.map((config) =>
    backfillParameterTimeSeriesUnit(config, catalog, families),
  );
}

export function stripDisplayGroupParametersWithoutTimeSeries(
  groups: ObservationsMapParameterDisplayGroup[] | undefined,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[] | undefined,
): ObservationsMapParameterDisplayGroup[] {
  if (!Array.isArray(groups)) return [];

  const configuredIds = configuredTimeSeriesParameterIdSet(timeSeriesConfigurations ?? []);

  return groups
    .map((group) => ({
      ...group,
      parameter_ids: group.parameter_ids.filter((id) => configuredIds.has(id)),
    }))
    .filter((group) => group.parameter_ids.length > 0);
}

export function formatUnitLabel(unit: ParametersUnit | undefined): string {
  if (!unit) return "";
  return unit.name ?? unit.symbol ?? unit.code;
}
